// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll, { FancyScrollRefProps } from "@tps/App/Components/Common/FancyScroll";
import { xScreen } from "@tps/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    // XNG-Changes
    wrapper?: InjectableCss;
    imageBadgeGridItem?: GridItemProps;
    productImageBadgesStyles?: ProductImageBadgesStyles;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 270px;
        `,
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            width: 270px;
            text-align: left;
            margin: 5px 0 0 0;
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;
    private autocompleteColumnProductParentMenu = React.createRef<HTMLUListElement>();
    private fancyScrollRef = React.createRef<FancyScrollRefProps>();

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    // XNG-Changes add badges
    getProductBadges = (product: ProductAutocompleteItemModel) => {
        if (!product?.properties?.badges) {
            return null;
        }
        const badges = JSON.parse(product.properties.badges);

        if (
            product.properties.previouslyPurchased?.toLowerCase() === "true" &&
            product.properties.previouslyPurchasedBadge
        ) {
            badges.push(JSON.parse(product.properties.previouslyPurchasedBadge));
        }
        return badges;
    };

    render() {
        const { products } = this.props;
        const styles = this.styles;
        this.fancyScrollRef?.current?.updateScrollable();
        return (
            <>
                <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteProducts_Header"
                    className="autocomplete-products-header"
                >
                    {`${translate("Products")} (${products.length})`}
                </Typography>
                {/* XNG-Changes: add fancyScroll with wrapper */}
                <FancyScroll
                    refElement={this.autocompleteColumnProductParentMenu}
                    ref={this.fancyScrollRef}
                    extendedStyles={{
                        css: css`
                            height: calc(100% - 42px);
                            ${xScreen("lte", "md")} {
                                height: auto;
                                max-height: unset;
                            }
                        `,
                    }}
                >
                    <StyledWrapper ref={this.autocompleteColumnProductParentMenu as any} {...styles.wrapper}>
                        {products.map(product => {
                            const imageBadges = this.getProductBadges(product);
                            return (
                                <GridContainer
                                    {...(this.props.focusedItem === product
                                        ? styles.focusedContainer
                                        : styles.container)}
                                    key={product.id!}
                                    onClick={() => {
                                        this.props.goToUrl(product.url);
                                    }}
                                    className="autocomplete-products-container"
                                >
                                    <GridItem
                                        {...styles.imageGridItem}
                                        className="autocomplete-products-image-grid-item"
                                    >
                                        <LazyImage
                                            {...styles.image}
                                            altText={product.name}
                                            src={product.image}
                                            className="autocomplete-products-image"
                                        />
                                    </GridItem>
                                    <GridItem {...styles.infoGridItem} className="autocomplete-products-info-grid-item">
                                        <Link
                                            {...styles.titleLink}
                                            data-test-selector={`productId_${product.id}`}
                                            className="autocomplete-products-link"
                                            aria-label={product.title}
                                        >
                                            {product.displayTitle}
                                        </Link>
                                        {/* XNG-Changes */}
                                        {/* <Typography
                                            {...styles.erpNumberText}
                                            data-test-selector="autocompleteProducts_PartNumber"
                                            className="autocomplete-products-erp-number"
                                        >
                                            {product.isNameCustomerOverride ? (
                                                <>
                                                    {translate("My Part #")}
                                                    {product.displayName}
                                                </>
                                            ) : (
                                                <>
                                                    {translate("Part #")}
                                                    {product.displayErpNumber}
                                                </>
                                            )}
                                            {product.manufacturerItemNumber && (
                                                <>
                                                    &nbsp;
                                                    {translate("MFG #")}
                                                    {product.displayManufacturerItemNumber}
                                                </>
                                            )}
                                        </Typography> */}
                                    </GridItem>
                                    {imageBadges && (
                                        <GridItem {...styles.imageBadgeGridItem}>
                                            <ProductImageBadges
                                                badgeList={imageBadges}
                                                maxBadges={3}
                                                numberOfColumns={3}
                                                extendedStyles={styles.productImageBadgesStyles}
                                            />
                                        </GridItem>
                                    )}
                                </GridContainer>
                            );
                        })}
                    </StyledWrapper>
                </FancyScroll>
            </>
        );
    }
}

export default AutocompleteProducts;
